"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import SafeHtml from "@/components/ui/SafeHtml";
import { Button } from "@/components/ui/Button";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import type { FeaturedEventsFields } from "@/types/acf";
import {
  type FooEvents,
  type FooEventsSlots,
  getEventStart,
  getEventEnd,
  getEventDateLabel,
  isUpcoming,
} from "@/lib/eventDateHelpers";

type RenderedField = { rendered?: string };
type WpTerm = { id?: number; name?: string; slug?: string };

export type EventProduct = {
  id?: number;
  ID?: number;
  slug?: string;
  link?: string;
  post_title?: string;
  featured_image?: string;
  title?: RenderedField;
  fooevents?: FooEvents | null;
  fooevents_slots?: FooEventsSlots | null;
  is_free?: boolean;
  product_cat?: string[];
  _embedded?: {
    "wp:featuredmedia"?: Array<{
      alt_text?: string;
      source_url?: string;
      media_details?: {
        sizes?: {
          medium_large?: { source_url?: string };
          medium?: { source_url?: string };
          large?: { source_url?: string };
        };
      };
    }>;
    "wp:term"?: Array<WpTerm[]>;
  };
};

const BASE = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/$/, "");

function withBase(u?: string | null): string | null {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  if (!BASE) return u.startsWith("/") ? u : `/${u}`;
  const normalized = u.startsWith("/") ? u : `/${u}`;
  const originUrl = new URL(BASE);
  const originPath = originUrl.pathname.replace(/\/$/, "");
  if (originPath && normalized.startsWith(originPath + "/")) {
    return originUrl.origin + normalized;
  }
  return `${BASE}${normalized}`;
}

function getProductTitle(p: EventProduct): string {
  const raw = p.title?.rendered || p.post_title || "";
  return raw
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/&#(\d+);/g, (_, c) => String.fromCharCode(Number(c)))
    .replace(/&amp;/g, "&")
    .replace(/&ndash;/g, "–")
    .replace(/&#8211;/g, "–");
}

function getImageSrc(p: EventProduct): string | null {
  const media = p._embedded?.["wp:featuredmedia"]?.[0];
  if (media) {
    return withBase(
      media.media_details?.sizes?.large?.source_url ||
      media.media_details?.sizes?.medium_large?.source_url ||
      media.media_details?.sizes?.medium?.source_url ||
      media.source_url
    );
  }
  return withBase(p.featured_image) ?? null;
}

function getHref(p: EventProduct): string {
  if (typeof p.link === "string" && p.link) {
    try {
      return new URL(p.link, "https://example.local").pathname;
    } catch { /* fall */ }
  }
  if (typeof p.slug === "string" && p.slug) return p.slug;
  return "#";
}

function getCategoryName(p: EventProduct): string {
  const terms = p._embedded?.["wp:term"];
  if (terms) {
    for (const group of terms) {
      if (Array.isArray(group) && group.length > 0) {
        const name = group[0]?.name;
        if (name && typeof name === "string") return name;
      }
    }
  }
  const first = (p.product_cat ?? [])[0];
  if (first && isNaN(Number(first))) return first;
  return "";
}

async function fetchProductsByIds(ids: number[], lang: string): Promise<EventProduct[]> {
  if (!BASE || !ids.length) return [];
  const url =
    `${BASE}/wp-json/bright/v1/products?` +
    `lang=${encodeURIComponent(lang)}&include=${ids.join(",")}&per_page=${ids.length}` +
    `&_embed=1&status=publish&include_fooevents=1`;
  try {
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) return [];
    const data = (await res.json()) as unknown;
    return Array.isArray(data) ? (data as EventProduct[]) : [];
  } catch { return []; }
}

// Filter on END date so currently-running multi-day events (e.g. exhibitions
// that started weeks ago but run for months) still surface. Sort by START
// ascending so already-running events appear before ones that haven't begun yet.
async function fetchSoonestUpcoming(lang: string, limit = 2): Promise<EventProduct[]> {
  if (!BASE) return [];
  const url =
    `${BASE}/wp-json/bright/v1/products?` +
    `lang=${encodeURIComponent(lang)}&per_page=100&_embed=1&status=publish&include_fooevents=1`;
  try {
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) return [];
    const data = (await res.json()) as unknown;
    const all = Array.isArray(data) ? (data as EventProduct[]) : [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();
    const seen = new Set<number>();
    const unique = all.filter((p) => {
      const id = p.id ?? p.ID ?? 0;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return unique
      .map((p) => ({ p, start: getEventStart(p), end: getEventEnd(p) }))
      .filter(
        (x): x is { p: EventProduct; start: Date; end: Date } =>
          x.start !== null && x.end !== null && x.end.getTime() >= todayMs
      )
      .sort((a, b) => a.start.getTime() - b.start.getTime())
      .slice(0, limit)
      .map((x) => x.p);
  } catch { return []; }
}

function EventCard({
  product, lang, position, priority,
}: {
  product: EventProduct;
  lang: string;
  position: 0 | 1;
  priority?: boolean;
}) {
  const href = getHref(product);
  const title = getProductTitle(product);
  const imgSrc = getImageSrc(product);
  const imgAlt = product._embedded?.["wp:featuredmedia"]?.[0]?.alt_text || title;
  const locale = lang === "pt" ? "pt-PT" : lang;
  const dateLabel = getEventDateLabel(product, locale);
  const category = getCategoryName(product);

  const isLeft = position === 0;
  const barBg = isLeft ? "bg-fuji-red" : "bg-fuji-green";
  const barSide = isLeft ? "left-0" : "right-0";
  const barTextStyle: React.CSSProperties = {
    writingMode: "vertical-rl",
    transform: isLeft ? "rotate(180deg)" : "none",
  };
  const contentPadding = category ? (isLeft ? "pl-12 pr-6" : "pl-6 pr-12") : "px-6";

  return (
    <article className="relative overflow-hidden group">
      <Link href={href} className="block">
        <div className="relative w-full aspect-square overflow-hidden bg-gray-900">
          {imgSrc ? (
            <Image
              src={imgSrc}
              alt={imgAlt}
              fill
              priority={priority}
              sizes="(max-width: 640px) 100vw, 50vw"
              className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
            />
          ) : (
            <div className="absolute inset-0 bg-gray-800" />
          )}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.3) 40%, transparent 65%)",
            }}
          />
          {category && (
            <div className={`absolute top-0 bottom-0 ${barSide} z-10`}>
              <div
                className={`${barBg} h-full flex ${isLeft ? "items-end" : "items-start"} justify-center`}
                style={{ width: "2rem" }}
              >
                <span
                  className="block text-white text-xs font-semibold uppercase tracking-widest py-4"
                  style={barTextStyle}
                >
                  {category}
                </span>
              </div>
            </div>
          )}
          <div className={`absolute bottom-0 left-0 right-0 z-10 pb-6 pt-3 ${contentPadding}`}>
            <h3 className="text-white text-xl font-bold leading-snug m-0 mb-1 drop-shadow">
              {title}
            </h3>
            {dateLabel && (
              <p className="text-white/80 text-sm m-0 drop-shadow">{dateLabel}</p>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}

export default function FeaturedEvents({
  data,
  lang = "en",
}: {
  data: FeaturedEventsFields;
  lang?: string;
}) {
  const prefetched = (data as unknown as { _prefetchedEvents?: EventProduct[] })
    ._prefetchedEvents;
  const hasPrefetched = Array.isArray(prefetched) && prefetched.length > 0;

  const featuredIds = React.useMemo(() => {
    const rows = (data as unknown as { featured_events?: unknown }).featured_events;
    if (!rows || !Array.isArray(rows)) return [];
    return rows
      .map((r) => (r?.event ? (r.event.ID ?? r.event.id ?? 0) : 0))
      .filter((id: number) => id > 0);
  }, [data]);

  const [eventsToShow, setEventsToShow] = React.useState<EventProduct[]>(
    hasPrefetched ? prefetched! : []
  );
  const [loading, setLoading] = React.useState(!hasPrefetched);

  React.useEffect(() => {
    if (hasPrefetched) return;

    let cancelled = false;

    async function load() {
      setLoading(true);

      if (featuredIds.length > 0) {
        const products = await fetchProductsByIds(featuredIds, lang);
        if (cancelled) return;

        if (products.length > 0) {
          const byId = new Map(products.map((p) => [p.id ?? p.ID ?? 0, p]));
          const seen = new Set<number>();
          const ordered = featuredIds
            .map((id: number) => byId.get(id))
            .filter((p): p is EventProduct => {
              if (!p) return false;
              const id = p.id ?? p.ID ?? 0;
              if (seen.has(id)) return false;
              seen.add(id);
              return true;
            });

          // Keep any picks that haven't ended yet. Previously `.every(isUpcoming)`
          // discarded the whole list if one pick was past.
          const stillValid = ordered.filter(isUpcoming);
          if (stillValid.length > 0) {
            setEventsToShow(stillValid.slice(0, 2));
            setLoading(false);
            return;
          }
        }
      }

      const soonest = await fetchSoonestUpcoming(lang, 2);
      if (!cancelled) {
        setEventsToShow(soonest);
        setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [featuredIds, lang, hasPrefetched]);

  if (!data || data.hide) return null;

  const title = data.title ?? "";
  const description = data.description ?? "";

  return (
    <div id={data.id}>
      {title && (
        <h2 className="text-title-2 text-center mb-6 font-semibold">
          <SafeHtml content={title} />
        </h2>
      )}
      {loading ? (
        <div className="grid gap-6 sm:grid-cols-2">
          {[0, 1].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="w-full aspect-square bg-gray-100" />
            </div>
          ))}
        </div>
      ) : eventsToShow.length > 0 ? (
        <div className="grid gap-6 sm:grid-cols-2">
          {eventsToShow.map((product, i) => (
            <EventCard
              key={product.ID ?? product.id ?? i}
              product={product}
              lang={lang}
              position={i === 0 ? 0 : 1}
              priority={i === 0}
            />
          ))}
        </div>
      ) : null}
      {description && (
        <div className="mt-12 min-w-0 break-words">
          <SafeHtml content={description} flow />
        </div>
      )}
      {data.button && typeof data.button === "object" && data.button.url ? (
        <div className="mt-8 flex justify-center">
          <Button
            as="a"
            href={toAbsoluteUrl(data.button.url) ?? "#"}
            target={data.button.target || "_self"}
            rel={data.button.target === "_blank" ? "noopener noreferrer" : undefined}
          >
            {data.button.title || "Ver mais"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}